/**
 * Incoming table invitations.
 *
 * Not `toast.info`: the shared toast is a message that leaves on its own, and an
 * invitation is a question with two answers. Unirse navigates to the room the
 * invite names — acceptance is the ordinary join by room id, with no new
 * authorisation path — and Ahora no simply drops it.
 *
 * The expiry is the server's; this only stops offering a Unirse that would be
 * refused, by re-rendering on a slow tick rather than a timer per invite.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useStore } from '../../store';
import { liveInvites } from '../../store/social';
import { Button } from '../../ui/Button';

import styles from './InviteToasts.module.css';

const TICK_MS = 1000;

export function InviteToasts() {
  const { t } = useTranslation('social');
  const navigate = useNavigate();
  const invites = useStore((s) => s.social.invites);
  const dismiss = useStore((s) => s.social.dismissInvite);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (invites.length === 0) return;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [invites.length]);

  const live = liveInvites(invites, now);
  if (live.length === 0) return null;

  return (
    <div className={styles.stack} data-testid="invite-stack">
      {live.map((invite) => (
        <div
          key={invite.inviteId}
          className={styles.invite}
          // An invitation interrupts: it has a deadline, and a polite live
          // region would announce it after whatever else is being read.
          role="alert"
          data-testid="invite-toast"
        >
          <img
            className={styles.avatar}
            src={
              invite.from.avatarUrl === ''
                ? '/avatars/presets/default-01.svg'
                : invite.from.avatarUrl
            }
            alt=""
            width={36}
            height={36}
          />
          <div className={styles.body}>
            <span className={styles.headline}>
              {t('invite.incoming', { name: invite.from.username })}
            </span>
            <span className={styles.detail}>
              {t('invite.table', {
                name: invite.roomName,
                points: invite.config.targetPoints,
              })}
            </span>
          </div>
          <div className={styles.actions}>
            <Button
              size="sm"
              variant="primary"
              data-testid="invite-join"
              onClick={() => {
                dismiss(invite.inviteId);
                void navigate(`/room/${invite.roomId}`);
              }}
            >
              {t('invite.join')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              data-testid="invite-ignore"
              onClick={() => {
                dismiss(invite.inviteId);
              }}
            >
              {t('invite.ignore')}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
